// src/pages/dashboard/admin/users/Users.jsx
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../../../lib/api';
import LoadingSpinner from '../../../../components/common/LoadingSpinner';
import { Ban, CheckCircle, XCircle, Shield } from 'lucide-react';
import AssignAgentModal from './AssignAgentModal';
import Button from '../../../../components/layout/ui/Button';
import { success, error } from '../../../../components/common/Toast';
import { useConfirm } from '../../../../hooks/useConfirm';

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [assignModal, setAssignModal] = useState({ open: false, nanny: null });

  const { confirm } = useConfirm();

  useEffect(() => {
    fetchUsers();
  }, [search, roleFilter]);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/admin/users', {
        params: { search, role: roleFilter === 'all' ? null : roleFilter }
      });
      setUsers(res.data.data.data);
    } catch {
      error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const toggleBan = async (id) => {
    setLoadingBanUser(true)
    try {
      await api.post(`/admin/ban/${id}`);
      setUsers(prev => prev.map(u => u.id === id ? { ...u, is_banned: !u.is_banned } : u));
      success('User status updated');
    } catch {
      error('Action failed');
      setLoadingBanUser(false)
    }finally {
      setLoadingBanUser(false)
    }
  };

  const toggleActive = async (id) => {
    setLoadingDeactivateUser(true)
    try {
      await api.post(`/admin/toggle-active/${id}`);
      setUsers(prev => prev.map(u => u.id === id ? { ...u, is_active: !u.is_active } : u));
      success('Account status updated');
    } catch {
      error('Action failed');
      setLoadingDeactivateUser(false)
    } finally {
      setLoadingDeactivateUser(false)
    }
  };

  const [loadingVetNanny, setLoadingVetNanny] = useState(false)
  const [loadingBanUser, setLoadingBanUser] = useState(false)
  const [loadingDeactivateUser, setLoadingDeactivateUser] = useState(false)

  const vetNanny = async (id) => {
    setLoadingVetNanny(true)
    const confirmed = await confirm({
      title: "Vet This Nanny?",
      message: "Once vetted, this cannot be reversed. This nanny will be marked as fully verified.",
      confirmText: "Yes, Mark as Vetted",
      cancelText: "Cancel",
      danger: false,
    });

    if (!confirmed) return;

    try {
      await api.post(`/agent/nannies/${id}/complete-vetting`);
      
      setUsers(prev => prev.map(u => u.id === id ? { ...u, is_vetted: true } : u));
      success('Nanny vetted successfully!');
    } catch {
      error('Failed to vet nanny');
    }finally {
    setLoadingVetNanny(false)

    }
  };

  if (loading) return <LoadingSpinner size="lg" />;

  return (
    <div className="max-w-7xl mx-auto space-y-8 py-8">
      <h1 className="text-5xl font-black text-center">User Management</h1>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-6 justify-between items-center">
        <input
          type="text"
          placeholder="Search by name or phone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full sm:w-96 px-6 py-4 rounded-2xl border-2 border-gray-200 focus:border-primary focus:ring-4 focus:ring-primary/20 transition"
        />
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="px-6 py-4 rounded-2xl border-2 border-gray-200 focus:border-primary"
        >
          <option value="all">All Roles</option>
          <option value="nanny">Nannies</option>
          <option value="customer">Customers</option>
          <option value="agent">Agents</option>
          <option value="admin">Admins</option>
        </select>
      </div>

      {/* Users Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {users.map((user) => {
          const assignedAgent = user.agents?.[0] ?? null;

          return (
            <div key={user.id} className="group">
              {/* Clickable Card - Only top part goes to profile */}
              <Link
                to={`/dashboard/profile/${user.id}`}
                className="block"
                onClick={(e) => {
                  // Prevent navigation when clicking buttons
                  if (e.target.closest('button')) {
                    e.preventDefault();
                  }
                }}
              >
                <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100 hover:shadow-2xl hover:border-primary/30 transition-all duration-300">
                  {/* Header */}
                  <div className="flex items-center gap-6 mb-6">
                    <div className="w-24 h-24 bg-gradient-to-br from-primary to-secondary rounded-3xl flex items-center justify-center text-white text-4xl font-black shadow-lg overflow-hidden">
                      {user.profile_picture && !user.profile_picture.includes('default-avatar') ? (
                        <img 
                          src={process.env.REACT_APP_IMAGE_URL + "storage/" + user.profile_picture} 
                          alt={user.first_name} 
                          className="w-full h-full object-cover" 
                        />
                      ) : (
                        user.first_name[0]
                      )}
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold group-hover:text-primary transition">
                        {user.first_name} {user.last_name}
                      </h3>
                      <p className="text-gray-600 capitalize">{user.profile_type}</p>
                      <p className="text-sm text-gray-500">{user.phone_number}</p>
                    </div>
                  </div>

                  {/* Assigned Agent */}
                  {user.profile_type === 'nanny' && assignedAgent && (
                    <div className="bg-blue-50 p-4 rounded-xl mb-6">
                      <p className="text-sm font-medium text-blue-800">
                        Assigned to: <span className="font-bold">{assignedAgent.first_name} {assignedAgent.last_name}</span>
                      </p>
                    </div>
                  )}

                  {/* Status Badges */}
                  <div className="flex flex-wrap gap-3 mb-8">
                    {user.is_vetted && (
                      <span className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-xl text-sm font-bold">
                        <Shield size={18} /> Vetted
                      </span>
                    )}
                    {user.is_banned && (
                      <span className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-xl text-sm font-bold">
                        <Ban size={18} /> Banned
                      </span>
                    )}
                    {!user.is_active && (
                      <span className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl text-sm font-bold">
                        <XCircle size={18} /> Inactive
                      </span>
                    )}
                  </div>
                </div>
              </Link>

              {/* Action Buttons - OUTSIDE the Link so they don't trigger navigation */}
              <div className="mt-4 grid grid-cols-2 gap-4 px-8">
                <Button
                  variant={user.is_active ? 'danger' : 'primary'}
                  loading={loadingDeactivateUser}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleActive(user.id);
                  }}
                  className="text-lg"
                >
                  {user.is_active ? 'Deactivate' : 'Activate'}
                </Button>

                <Button
                  variant={user.is_banned ? 'outline' : 'danger'}
                  loading={loadingBanUser}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleBan(user.id);
                  }}
                  className="text-lg"
                >
                  {user.is_banned ? 'Unban' : 'Ban User'}
                </Button>

                {user.profile_type === 'nanny' && !user.is_vetted && (
                  <Button
                    variant="primary"
                    loading={loadingVetNanny}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      vetNanny(user.id);
                    }}
                    className="col-span-2 text-lg"
                  >
                    Vet Nanny
                  </Button>
                )}

                {user.profile_type === 'nanny' && (
                  <Button
                    variant="outline"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setAssignModal({ open: true, nanny: user });
                    }}
                    className="col-span-2 text-lg"
                  >
                    {assignedAgent ? 'Reassign Agent' : 'Assign Agent'}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Assign Agent Modal */}
      <AssignAgentModal
        nanny={assignModal.nanny}
        isOpen={assignModal.open}
        onClose={() => setAssignModal({ open: false, nanny: null })}
        onSuccess={fetchUsers}
      />
    </div>
  );
}